import type { PropsWithChildren } from "react";
import { LoaderCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthSession } from "./AuthSessionProvider";

export function ProtectedRoute({ children }: PropsWithChildren) {
  const auth = useAuthSession();
  const location = useLocation();
  if (auth.status === "loading") return <SessionScreen loading />;
  if (auth.status === "error") return <SessionScreen error={auth.error.message} retry={() => void auth.refresh()} />;
  if (auth.status === "anonymous") {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }
  return children;
}

export function AnonymousOnlyRoute({ children }: PropsWithChildren) {
  const auth = useAuthSession();
  const location = useLocation();
  if (auth.status === "loading") return <SessionScreen loading />;
  if (auth.status === "error") return <SessionScreen error={auth.error.message} retry={() => void auth.refresh()} />;
  if (auth.status === "authenticated") return <Navigate to={returnTarget(location.search)} replace />;
  return children;
}

export function returnTarget(search: string): string {
  const value = new URLSearchParams(search).get("returnTo");
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/today";
  if (["/login", "/signup", "/forgot-password", "/reset-password"].some((path) => value.startsWith(path))) return "/today";
  return value;
}

function SessionScreen({ loading, error, retry }: { loading?: boolean; error?: string; retry?: () => void }) {
  return <main className="session-screen" role={error ? "alert" : "status"} aria-live="polite">
    <div className="session-card">
      <div className="session-icon">{loading ? <LoaderCircle className="spin" size={24} /> : <ShieldCheck size={24} />}</div>
      <h1>{loading ? "Checking your session" : "Unable to check your session"}</h1>
      <p>{loading ? "Securely restoring your personal session…" : error}</p>
      {retry && <button className="button primary" onClick={retry}><RefreshCw size={15} />Retry</button>}
    </div>
  </main>;
}
